import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ATTACHMENT_MAX_FILE_BYTES, ATTACHMENT_MAX_PER_PARENT } from '@/common/utils/Constants/Constants'

export const UPLOAD_STATE = {
    held: 'held',
    waiting: 'waiting',
    uploading: 'uploading',
    checking: 'checking',
    // Sent and stored. The row stays until the refreshed list takes over, so nothing jumps mid-batch.
    done: 'done',
    error: 'error',
}

// The file is stored and holds a slot; the refreshed list shows it as failed or still pending.
// Sending it again would store a duplicate.
const isStoredWithoutVerdict = ({ sent, status }) =>
    status === 403 || status === 504 || status === 524 || (status === 'TIMEOUT' && sent)

/**
 * useAttachmentUploads - The upload queue behind a documents panel. Each file goes in its own
 * request, one after the other, so one failure never sinks the rest, and each outcome can name its file. Files that
 * land (or land without a verdict) leave the queue once the list refreshes; files that need the
 * user stay listed with the reason and, where sending again is safe, a retry.
 *
 * @param {Object} params
 * @param {number} params.usedSlots - Attachments already on the parent, in any scan state
 * @param {Function} params.onUploadFile - (file, { onProgress }) => Promise, rejects with
 *   `{ status, data, retryAfter, sent }` (see uploadAttachmentFile)
 * @param {Function} params.onUploadsSettled - Called once per batch to refresh the list. It must work
 *   after the panel unmounts (a dialog can close mid-upload), so refresh through the store, not a
 *   query hook's refetch.
 */
const useAttachmentUploads = ({ onUploadFile, onUploadsSettled, usedSlots }) => {
    const { t } = useTranslation()
    const [items, setItems] = useState([])
    const [inFlight, setInFlight] = useState(false)
    // Keys of the files in the batch being sent, so the summary can say "2 of 4".
    const [batchKeys, setBatchKeys] = useState([])
    const nextKey = useRef(0)

    const freeSlots = Math.max(0, ATTACHMENT_MAX_PER_PARENT - usedSlots)
    const held = items.filter(({ state }) => state === UPLOAD_STATE.held)
    const excess = Math.max(0, held.length - freeSlots)

    const update = (key, changes) =>
        setItems((current) => current.map((item) => (item.key === key ? { ...item, ...changes } : item)))

    const remove = (key) => setItems((current) => current.filter((item) => item.key !== key))

    const errorFor = (name, error) => {
        if (error.status === 429) {
            return {
                canRetry: true,
                message: error.retryAfter
                    ? t('Too many uploads right now. Wait {{seconds}} seconds, then retry {{name}}.', {
                          name,
                          seconds: error.retryAfter,
                      })
                    : t('Too many uploads right now. Wait a moment, then retry {{name}}.', { name }),
            }
        }
        // The upload bypasses the app's 401 redirect (see uploadAttachmentFile), so say it here.
        if (error.status === 401) {
            return {
                canRetry: false,
                message: t('You have been signed out, so {{name}} was not uploaded. Sign in again and add it.', {
                    name,
                }),
            }
        }
        if (error.status === 400 || error.status === 404) {
            return { canRetry: false, message: error.data ?? t('{{name}} was refused.', { name }) }
        }
        if (error.status === 'NETWORK' && error.sent) {
            return {
                canRetry: false,
                message: t(
                    'The connection dropped while {{name}} was being checked. Refresh the page to see whether it was accepted.',
                    { name }
                ),
            }
        }
        if (error.status === 'NETWORK' || error.status === 'TIMEOUT') {
            return {
                canRetry: true,
                message: t('{{name}} could not be uploaded. Check your connection and try again.', { name }),
            }
        }
        return { canRetry: true, message: t('{{name}} could not be uploaded. Try again.', { name }) }
    }

    const send = async (batch) => {
        setInFlight(true)
        setBatchKeys(batch.map(({ key }) => key))
        const keys = new Set(batch.map(({ key }) => key))
        setItems((current) =>
            current.map((item) => (keys.has(item.key) ? { ...item, state: UPLOAD_STATE.waiting, message: null } : item))
        )

        // One file at a time: the server counts free slots, then uploads the blob, then stores the
        // row, with no lock in between. Files sent side by side could each pass the count.
        for (const { file, key } of batch) {
            update(key, { state: UPLOAD_STATE.uploading, progress: 0 })
            try {
                await onUploadFile(file, {
                    onProgress: (fraction) =>
                        update(
                            key,
                            fraction >= 1 ? { state: UPLOAD_STATE.checking } : { progress: Math.round(fraction * 100) }
                        ),
                })
                update(key, { state: UPLOAD_STATE.done, accepted: true })
            } catch (error) {
                if (isStoredWithoutVerdict(error)) update(key, { state: UPLOAD_STATE.done, accepted: false })
                else update(key, { state: UPLOAD_STATE.error, ...errorFor(file.name, error) })
            }
        }

        // A failed refresh leaves the old list in place; keep the finished rows until one lands.
        // (RTK Query's initiate() resolves with { error } rather than throwing.)
        let refreshed = false
        try {
            refreshed = !(await onUploadsSettled())?.error
        } finally {
            if (refreshed) setItems((current) => current.filter(({ state }) => state !== UPLOAD_STATE.done))
            setInFlight(false)
        }
    }

    const addFiles = (files) => {
        const added = files.map((file) => {
            const key = nextKey.current++
            if (file.size > ATTACHMENT_MAX_FILE_BYTES) {
                return {
                    key,
                    file,
                    state: UPLOAD_STATE.error,
                    canRetry: false,
                    // Never sent, so the row offers Dismiss rather than Remove.
                    tooLarge: true,
                    message: t('{{name}} is larger than {{maxSize}} MB and was not uploaded.', {
                        name: file.name,
                        maxSize: Math.round(ATTACHMENT_MAX_FILE_BYTES / (1024 * 1024)),
                    }),
                }
            }
            return { key, file, state: UPLOAD_STATE.held }
        })
        setItems((current) => [...current, ...added])

        // A selection that fits goes straight out; one that does not waits for the user to trim it.
        const nowHeld = [...held, ...added.filter(({ state }) => state === UPLOAD_STATE.held)]
        if (nowHeld.length && nowHeld.length <= freeSlots) send(nowHeld)
    }

    return {
        addFiles,
        batchKeys,
        excess,
        freeSlots,
        heldCount: held.length,
        inFlight,
        items,
        remove,
        // At the cap a retry would only earn the server's limit refusal.
        retry: (key) => freeSlots && send(items.filter((item) => item.key === key)),
        sendHeld: () => send(held),
    }
}

export default useAttachmentUploads
